/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-16 06:24:30
 *
 *******************************************************/
// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import { downloadFile } from 'src/helpers';

import apiService from 'src/services/api-service';
import {
  GET_SYNONYMS_REQUEST,
  GET_SYNONYMS_SUCCESS,
  GET_SYNONYMS_FAILURE,
  BULK_SYNONYMS_REQUEST,
  BULK_SYNONYMS_SUCCESS,
  BULK_SYNONYMS_FAILURE,
  DELETE_SYNONYMS_REQUEST,
  DELETE_SYNONYMS_SUCCESS,
  DELETE_SYNONYMS_FAILURE,
  UPDATE_SYNONYM_REQUEST,
  UPDATE_SYNONYM_SUCCESS,
  UPDATE_SYNONYM_FAILURE,
  VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_REQUEST,
  VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_SUCCESS,
  VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_FAILURE,
  IMPORT_FILE_SYNONYMS_REQUEST,
  IMPORT_FILE_SYNONYMS_SUCCESS,
  IMPORT_FILE_SYNONYMS_FAILURE,
  EXPORT_FILE_SYNONYMS_REQUEST,
  EXPORT_FILE_SYNONYMS_SUCCESS,
  EXPORT_FILE_SYNONYMS_FAILURE
} from 'stores/flow-type/synonym.type';
import { SynonymType } from 'src/constants/synonym';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to get synonyms
 * @return {object} The RSAA object for API middleware
 */
export const getSynonyms = (queries, i18n: I18n, type = SynonymType.MANUAL) => {
  return apiService.sendRequest(
    `admin-v3.${type}.get-synonyms`,
    { queries: { sort: '', status: '', ...queries } },
    [
      GET_SYNONYMS_REQUEST,
      {
        type: GET_SYNONYMS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data, type };
        }
      },
      {
        type: GET_SYNONYMS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Synonym.Actions.getSynonyms'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to bulk synonyms
 * @return {object} The RSAA object for API middleware
 */
export const bulkSynonyms = (synonyms, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.synonym.bulk-synonyms', { body: synonyms }, [
    BULK_SYNONYMS_REQUEST,
    {
      type: BULK_SYNONYMS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom-with-sync',
          i18n.translate('AdminHTML.Synonym.Actions.bulkSynonyms_Success'),
          i18n
        );

        mixpanelV3Helper.trackEventData('Add New Synonym', {
          feature: 'Synonym',
          synonymType: 'manual'
        });

        // Track Full Story event
        trackFSEvent('Add New Synonym', {
          feature: 'Synonym'
        });

        return { data };
      }
    },
    {
      type: BULK_SYNONYMS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Synonym.Actions.bulkSynonyms_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to delete synonyms
 * @return {object} The RSAA object for API middleware
 */
export const deleteSynonyms = (synonymIds, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.synonym.delete-synonyms', { queries: { synonymIds } }, [
    DELETE_SYNONYMS_REQUEST,
    {
      type: DELETE_SYNONYMS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom-with-sync',
          i18n.translate('AdminHTML.Synonym.Actions.deleteSynonyms_Success'),
          i18n
        );

        mixpanelV3Helper.trackEventData('Delete Synonym', {
          feature: 'Synonym',
          synonymType: 'manual'
        });

        // Track Full Story event
        trackFSEvent('Delete Synonym', {
          feature: 'Synonym'
        });

        return { data };
      }
    },
    {
      type: DELETE_SYNONYMS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Synonym.Actions.deleteSynonyms_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

export const deleteAISynonyms = (synonymId, i18n: I18n) => {
  return apiService.sendRequest(
    `admin-v3.ai-synonym.delete-synonyms`,
    { params: { synonym_id: synonymId } },
    [
      DELETE_SYNONYMS_REQUEST,
      {
        type: DELETE_SYNONYMS_SUCCESS,
        payload: async () => {
          return { synonymId };
        }
      },
      {
        type: DELETE_SYNONYMS_FAILURE,
        payload: async (action, state, res) => {
          return await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Synonym.Actions.deleteSynonyms_Failure'),
            i18n
          );
        }
      }
    ]
  );
};

/**
 * call to update synonyms
 * @return {object} The RSAA object for API middleware
 */
export const updateSynonyms = (synonyms, actionName = '', i18n: I18n) => {
  return apiService.sendRequest('admin-v3.synonym.update-synonym', { body: synonyms }, [
    UPDATE_SYNONYM_REQUEST,
    {
      type: UPDATE_SYNONYM_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom-with-sync',
          actionName === 'changeStatus'
            ? i18n.translate('AdminHTML.Synonym.Actions.updateSynonyms_Success_ChangeStatus')
            : i18n.translate('AdminHTML.Synonym.Actions.updateSynonyms_Success_NotChangeStatus'),
          i18n
        );

        if (actionName === 'changeStatus') {
          const eventNameMapping = {
            true: 'Turn on Synonym',
            false: 'Turn off Synonym'
          };
          mixpanelV3Helper.trackEventData(eventNameMapping[synonyms[0]?.enabled], {
            feature: 'Synonym',
            synonymType: 'manual'
          });

          // Track Full Story event
          trackFSEvent(eventNameMapping[synonyms[0]?.enabled], {
            feature: 'Synonym'
          });
        } else {
          mixpanelV3Helper.trackEventData('Edit Synonym', {
            feature: 'Synonym',
            synonymType: 'manual'
          });

          // Track Full Story event
          trackFSEvent('Edit Synonym', {
            feature: 'Synonym'
          });
        }

        return { data, actionName };
      }
    },
    {
      type: UPDATE_SYNONYM_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          actionName === 'changeStatus'
            ? i18n.translate('AdminHTML.Synonym.Actions.updateSynonyms_Failure_ChangeStatus')
            : i18n.translate('AdminHTML.Synonym.Actions.updateSynonyms_Failure_NotChangeStatus'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

export const updateAISynonyms = (synonym, actionName = '', i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.ai-synonym.update-synonym',
    { body: { status: synonym.status }, params: { synonym_id: synonym._id } },
    [
      UPDATE_SYNONYM_REQUEST,
      {
        type: UPDATE_SYNONYM_SUCCESS,
        payload: async () => {
          if (actionName === 'changeAIStatus') {
            const eventNameMapping = {
              enable: 'Turn on AI Synonym',
              disable: 'Turn off AI Synonym'
            };
            mixpanelV3Helper.trackEventData(eventNameMapping[synonym.status], {
              feature: 'Synonym',
              synonymType: 'AI',
              confidenceScore: synonym?.confidence
            });

            // Track Full Story event
            trackFSEvent(eventNameMapping[synonym.status], {
              feature: 'Synonym',
              synonymType: 'AI',
              confidenceScore: synonym?.confidence
            });
          }

          return { data: synonym, actionName };
        }
      },
      {
        type: UPDATE_SYNONYM_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Synonym.Actions.updateSynonyms_Failure_ChangeStatus'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to verify file content before import
 * @return {object} The RSAA object for API middleware
 */
export const verifyFileContent = files => {
  return apiService.sendRequest(
    'admin-v3.synonym.verify-import-synonyms',
    { attachements: files },
    [
      VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_REQUEST,
      {
        type: VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_FAILURE,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { ...data };
        }
      }
    ]
  );
};

/**
 * call to import file content
 * @return {object} The RSAA object for API middleware
 */
export const importFile = (files, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.synonym.import-synonyms', { attachements: files }, [
    IMPORT_FILE_SYNONYMS_REQUEST,
    {
      type: IMPORT_FILE_SYNONYMS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        mixpanelV3Helper.trackEventData('Import synonym list', {
          feature: 'Synonym',
          synonymType: 'manual'
        });

        // Track Full Story event
        trackFSEvent('Import synonym list', {
          feature: 'Synonym'
        });

        return { data };
      }
    },
    {
      type: IMPORT_FILE_SYNONYMS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Synonym.Actions.importFile'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to download file content
 * @return {object} The RSAA object for API middleware
 */
export const exportFile = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.synonym.export-synonyms', {}, [
    EXPORT_FILE_SYNONYMS_REQUEST,
    {
      type: EXPORT_FILE_SYNONYMS_SUCCESS,
      payload: async (action, state, res) => {
        // Convert response data into blob and download
        const blob = await res.blob();
        downloadFile(blob, 'All synonyms data.csv');

        const data = await getJSON(res);

        mixpanelV3Helper.trackEventData('Export synonym list', {
          feature: 'Synonym',
          synonymType: 'manual'
        });

        // Track Full Story event
        trackFSEvent('Export synonym list', {
          feature: 'Synonym'
        });

        return { data };
      }
    },
    {
      type: EXPORT_FILE_SYNONYMS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Synonym.Actions.exportFile'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};
